import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import DataTable from '../../../components/ui/DataTable';
import OptionsMenu from '../../../components/ui/OptionsMenu';
import Button from '../../../components/ui/Button';
import CheckboxInput from '../../../components/ui/CheckboxInput';
import ReminderDrawer from '../../performance-reminders/ReminderDrawer';
import { Badge, PageEmpty, Sub } from '../../performance-operations/styles';
import { usePanel } from '../../performance-operations/config';
import { formatDate } from '../../../utils/Utils';
import PrepareMeetingDrawer from './PrepareMeetingDrawer';
import {
  FILL_FEEDBACK_PATH, PAGE_SIZE, TEAM_GOALS_PATH, fetchTeamRows, reminderCriteria, taskCountsText,
} from '../config';

// PM Analitik Faz 2 (spec §9.3) — E17 Ekip sekmesi: sunucu tarafında sayfalı DataTable (EmployeesPage aynası; başlık yalnız
// tablonun title'ında; tek kaydırma — gövde yüksekliği ölçülür, yalnız satırlar kayar). Sıra sunucuda: gecikmiş görev azalan,
// sonra ad. Satır: İncele, Görüşme Hazırla (yalnız doğrudan ast), Değerlendirme Hazırla, Hatırlat. Araç çubuğu: Ekibe Hatırlat
// (çekmecede "Yalnız Gecikmiş"). Puan ve 360 değerlendirici kimliği bu listede hiç yoktur (sunucu sözleşmesi).
export default function TeamTable({ filters, context, containerHeight }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const history = useHistory();
  const filterKey = JSON.stringify([filters.periodId, filters.team, filters.due, filters.titleIds, filters.stage, filters.taskStatuses]);
  const [page, setPage] = useState({ pageNumber: 1, pageSize: PAGE_SIZE });
  const [refreshKey, setRefreshKey] = useState(0);
  useEffect(() => { setPage((p) => ({ ...p, pageNumber: 1 })); }, [filterKey]);

  const list = usePanel(
    () => fetchTeamRows(filters, page.pageNumber, page.pageSize),
    [filterKey, page.pageNumber, page.pageSize, refreshKey],
    !!filters.periodId,
  );
  const rows = useMemo(() => list.data?.data || [], [list.data]);

  // Hatırlatma çekmecesi: null = kapalı; { assigneeEmployeeId } satırdan (o kişinin görevleri), {} araç çubuğundan (ekip).
  const [reminder, setReminder] = useState(null);
  const [onlyOverdue, setOnlyOverdue] = useState(false);
  const [meetingFor, setMeetingFor] = useState(null);
  const managerEmployeeId = context?.managerEmployeeId;
  const selection = useMemo(() => (reminder ? {
    periodId: filters.periodId,
    criteria: reminderCriteria(filters, managerEmployeeId, { onlyOverdue, assigneeEmployeeId: reminder.assigneeEmployeeId || null }),
  } : null), [reminder, onlyOverdue, filters, managerEmployeeId]);

  const openReminder = useCallback((assigneeEmployeeId = null) => {
    setOnlyOverdue(false);
    setReminder({ assigneeEmployeeId });
  }, []);

  const handleTableChange = useCallback((action, tableState) => {
    if (action === 'changePage' || action === 'changeRowsPerPage') {
      setPage({ pageNumber: (tableState.page || 0) + 1, pageSize: tableState.rowsPerPage || PAGE_SIZE });
    }
  }, []);

  const columns = useMemo(() => [
    { name: 'participantId', label: 'ID', options: { filter: false, sort: false, display: 'excluded' } },
    {
      name: 'fullName',
      label: t('performance.workspace.team.col.employee'),
      options: {
        filter: false,
        sort: false,
        customBodyRender: (_, meta) => {
          const row = rows[meta.rowIndex];
          if (!row) return null;
          return (
            <div data-row={row.participantId}>
              <div>{row.fullName}</div>
              {row.titleName || !row.isDirect ? (
                <Sub>{[row.titleName, row.isDirect ? null : t('performance.workspace.team.col.indirect')].filter(Boolean).join(' · ')}</Sub>
              ) : null}
            </div>
          );
        },
      },
    },
    {
      name: 'employeeTasks',
      label: t('performance.workspace.team.col.employeeTasks'),
      options: {
        filter: false,
        sort: false,
        customBodyRender: (v) => <Badge $tone={v?.overdue > 0 ? 'danger' : undefined} data-cell="employeeTasks">{taskCountsText(t, v)}</Badge>,
      },
    },
    {
      name: 'managerTasks',
      label: t('performance.workspace.team.col.managerTasks'),
      options: {
        filter: false,
        sort: false,
        customBodyRender: (v) => <Badge $tone={v?.overdue > 0 ? 'danger' : undefined} data-cell="managerTasks">{taskCountsText(t, v)}</Badge>,
      },
    },
    {
      name: 'staleGoals',
      label: t('performance.workspace.team.col.staleGoals'),
      options: { filter: false, sort: false, customBodyRender: (v) => (v > 0 ? <Badge $tone="danger">{v}</Badge> : '0') },
    },
    {
      name: 'lastOneOnOneAt',
      label: t('performance.workspace.team.col.lastOneOnOne'),
      options: { filter: false, sort: false, customBodyRender: (v) => (v ? formatDate(v, lang) : '—') },
    },
    {
      name: 'resultPublished',
      label: t('performance.workspace.team.col.shared'),
      options: {
        filter: false,
        sort: false,
        customBodyRender: (v) => (v ? <Badge $tone="success">{t('performance.workspace.team.col.published')}</Badge> : '—'),
      },
    },
    {
      name: 'resultSeenAt',
      label: t('performance.workspace.team.col.seen'),
      options: { filter: false, sort: false, customBodyRender: (v) => (v ? formatDate(v, lang) : '—') },
    },
    {
      name: 'actions',
      label: ' ',
      options: {
        filter: false,
        sort: false,
        download: false,
        print: false,
        customBodyRender: (_, meta) => {
          const row = rows[meta.rowIndex];
          if (!row) return null;
          return (
            <OptionsMenu
              row={row}
              actions={[
                { type: 'view', label: t('performance.workspace.team.action.review'), onClick: () => history.push(TEAM_GOALS_PATH) },
                {
                  type: 'reschedule',
                  label: t('performance.workspace.team.action.prepareMeeting'),
                  condition: (r) => !!r.isDirect,
                  onClick: (r) => setMeetingFor(r),
                },
                { type: 'scorecard', label: t('performance.workspace.team.action.prepareReview'), onClick: () => history.push(FILL_FEEDBACK_PATH) },
                { type: 'send', label: t('performance.workspace.team.action.remind'), onClick: (r) => openReminder(r.employeeId) },
              ]}
            />
          );
        },
      },
    },
  ], [t, lang, rows, history, openReminder]);

  const toolbar = useMemo(() => (
    <Button variant="outline" onClick={() => openReminder(null)} disabled={!managerEmployeeId} data-action="remind-team">
      {t('performance.workspace.team.action.remindTeam')}
    </Button>
  ), [t, openReminder, managerEmployeeId]);

  const bodyHeight = containerHeight ? `${Math.max(containerHeight - 144, 200)}px` : undefined;

  return (
    <div data-section="team-table">
      {list.error ? (
        <PageEmpty data-state="error">
          <div>{t('performance.operations.panel.error')}</div>
          <Button variant="outline" onClick={list.retry} data-action="retry">{t('performance.operations.panel.retry')}</Button>
        </PageEmpty>
      ) : (
        <DataTable
          title={t('performance.workspace.team.table.title')}
          fetching={list.loading}
          columns={columns}
          data={rows}
          totalRowCount={list.data?.totalRowCount || 0}
          pageIndex={page.pageNumber - 1}
          pageSize={page.pageSize}
          onTableChanged={handleTableChange}
          toolbarActions={toolbar}
          disableFilters
          tableBodyMaxHeight={bodyHeight}
          customIdSelector="participantId"
        />
      )}
      <ReminderDrawer
        open={!!reminder}
        onClose={() => setReminder(null)}
        selection={selection}
        onSent={() => setRefreshKey((k) => k + 1)}
        headerContent={(
          <CheckboxInput
            label={t('performance.workspace.team.reminder.onlyOverdue')}
            checked={onlyOverdue}
            onChange={setOnlyOverdue}
            data-option="only-overdue"
          />
        )}
      />
      <PrepareMeetingDrawer
        row={meetingFor}
        onClose={() => setMeetingFor(null)}
        onCreated={() => setRefreshKey((k) => k + 1)}
      />
    </div>
  );
}
